import Alert from '@common/components/Alert/Alert';
import { BookableList, BookableListItemType } from '@features/List';
import { getBookingRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { toNightlyPrice } from '@features/rooms/helpers/toNightlyPrice.helper';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';
import moment from 'moment';
import { FC } from 'react';
import { useRouter } from 'shared-routing';
import { JourneyParams } from 'shared-types/Common';
import { RateType } from 'shared-types/Rate';

type RoomsListingProps = {
  rate: RateType;
  journeyParams: JourneyParams;
  isFetching?: boolean;
  allUpgradeRoomsInBasket: boolean;
};

export const RoomsListing: FC<RoomsListingProps> = ({
  rate,
  journeyParams,
  isFetching = false,
  allUpgradeRoomsInBasket,
}) => {
  const { query, pathname } = useRouter();
  const { t } = useTranslations();
  const linkCTA = t('room.bookRoom');
  const notAvailableTitle = t('rate.noRoomHeader');
  const notAvailableContent = t('rate.noRoomsInfo');

  const numberOfNights = moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');
  const { rooms = [] } = rate;
  const { rooms: _, ...journeyParamsNoRooms } = journeyParams;
  const items: BookableListItemType[] = rooms.map(
    ({
      id,
      imageUrl,
      name: title,
      description,
      pricePerStay,
      pricePerNight,
      roomUpgrade,
      discount,
    }) => ({
      id,
      imageUrl,
      title,
      description,
      pricePerStay,
      pricePerNight: discount
        ? toNightlyPrice(pricePerStay, discount, numberOfNights)
        : pricePerNight,
      discount,
      terms: '',
      numberOfNights,
      linkRoute: getBookingRoute(pathname),
      linkParams: {
        id: rate.id,
        room: id,
        ...journeyParamsNoRooms,
        promoRateCode: typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined,
        fromDate: parseDateToString(journeyParams.fromDate),
        toDate: parseDateToString(journeyParams.toDate),
        spaDate: parseDateToString(journeyParams.fromDate),
        bookingStep: roomUpgrade && !allUpgradeRoomsInBasket ? 'upgrade' : 'guestDetailsStep',
      },
      linkCTA,
      upgrade: roomUpgrade,
    })
  );

  return (
    <div style={{ opacity: isFetching ? 0.5 : 1 }}>
      {items.length ? (
        <BookableList items={items} numberOfRooms={journeyParams?.rooms?.length || 1} />
      ) : (
        <Alert title={notAvailableTitle} content={notAvailableContent} />
      )}
    </div>
  );
};
